import { LanguagesEnum } from '@/api/enums';
import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import { CourseChanges, CourseContentType, SlideImage } from '@/client/courses';
import { QuizType } from '@/client/quizzes';
import {
  PendingQuestionEdit,
  PendingSlideEdit,
} from '@/components/courses/course-changes/course-changes.types';
import { useToast } from '@/hooks/useToast';
import {
  convertQuestionsToArrayFormat,
  modifyCourseQuiz,
} from '@/utils/helpers/courses.helper';
import { AxiosError } from 'axios';

export const useSaveCourseChanges = (
  courseId: string | undefined,
  changes: CourseChanges | undefined,
  refetch: () => Promise<any>,
  language: LanguagesEnum,
) => {
  const toast = useToast();

  const saveSlideChanges = async (
    pendingSlideEdits: PendingSlideEdit[],
  ): Promise<void> => {
    if (!courseId || !changes?.customizedCourse) return;

    // Group slide edits by content index
    const slideEditsByContent = pendingSlideEdits.reduce((acc, edit) => {
      if (!acc[edit.contentIndex]) {
        acc[edit.contentIndex] = [];
      }
      acc[edit.contentIndex].push(edit);
      return acc;
    }, {} as Record<number, PendingSlideEdit[]>);

    // Process slide edits
    for (const [contentIndexStr, edits] of Object.entries(
      slideEditsByContent,
    )) {
      const contentIndex = parseInt(contentIndexStr, 10);
      const contentChange = changes.contentChanges[contentIndex];

      if (
        contentChange.type !== CourseContentType.PRESENTATION ||
        !contentChange.slideChanges
      ) {
        continue;
      }

      const courseContent = changes.customizedCourse.courseContents?.find(
        (cc) => cc.position === contentChange.position,
      );

      if (!courseContent?.presentation?.id) {
        continue;
      }

      const presentationId = courseContent.presentation.id;

      // Use existing presentation if it has translations, otherwise fetch it
      const presentation = courseContent.presentation;

      // Find translation for target language
      const translation = presentation.translations.find(
        (t) => t.language === language,
      );

      // Could not find the translation for the target language. Skip!
      if (!translation) {
        continue;
      }

      // Track slide positions (0-based) that have image text edits
      const imageEditedSlidePositions = new Set<number>();

      // Update slides based on edits
      for (const edit of edits) {
        const slideChange = contentChange.slideChanges[edit.slideIndex];
        if (!slideChange) continue;

        // Find slide by position (position in SlideChange is 1-based, slides are 0-based)
        const slidePosition = slideChange.position - 1;
        const slide = translation.slides.find(
          (s) => s.position === slidePosition,
        );

        if (!slide) {
          continue;
        }

        // Update the appropriate field
        switch (edit.field) {
          case 'headline':
            slide.headline = edit.value;
            break;
          case 'content':
            slide.html = edit.value;
            break;
          case 'footnote':
            slide.footnote = edit.value;
            break;
          case 'infobox':
            slide.infobox = edit.value;
            break;
          case 'imageTitle':
          case 'imageDescription': {
            if (edit.imageIndex === undefined) break;

            const imageTextChange =
              slideChange.imageTextChanges?.[edit.imageIndex];
            if (!imageTextChange) break;
            const image = slide.images?.find(
              (img) => img.position === imageTextChange.position,
            );
            if (!image) break;
            if (edit.field === 'imageTitle') {
              image.title = edit.value;
            } else {
              image.description = edit.value;
            }
            imageEditedSlidePositions.add(slidePosition);
            break;
          }
          case 'ieSender':
          case 'ieMessage':
          case 'ieHeader':
          case 'ieBody': {
            if (!slide.interactiveElement) break;
            type InteractiveElementData = {
              sender?: string;
              message?: string;
              header?: string;
              body?: string;
              [key: string]: unknown;
            };
            const data: InteractiveElementData = {
              ...((slide.interactiveElement.data as InteractiveElementData) ||
                {}),
            };
            if (edit.field === 'ieSender') data.sender = edit.value;
            if (edit.field === 'ieMessage') data.message = edit.value;
            if (edit.field === 'ieHeader') data.header = edit.value;
            if (edit.field === 'ieBody') data.body = edit.value;
            slide.interactiveElement = { ...slide.interactiveElement, data };
            break;
          }
        }
      }

      const presentationPayload = {
        ...presentation,
        translations: presentation.translations.map((trans) => ({
          ...trans,
          slides: trans.slides.map((slide) => {
            const { images, popupSlide, ...rest } = slide;

            const normalizedPopupSlide =
              typeof popupSlide === 'string' ? popupSlide : popupSlide?.id;

            const isTargetTranslation = trans.language === language;
            const hasImageEdits =
              isTargetTranslation &&
              slide.position !== undefined &&
              imageEditedSlidePositions.has(slide.position);

            if (hasImageEdits) {
              return {
                ...rest,
                popupSlide: normalizedPopupSlide || undefined,
                images: images?.map(({ file, ...imgRest }) => ({
                  ...imgRest,
                  file: file?.id ?? null,
                })) as unknown as SlideImage[],
              };
            }

            return {
              ...rest,
              popupSlide: normalizedPopupSlide || undefined,
              shouldSkipImages: true,
            };
          }),
        })),
      };

      await client.courses.updateSlideDeck(
        courseId,
        presentationId,
        presentationPayload,
      );
    }
  };

  const saveQuestionChanges = async (
    pendingQuestionEdits: PendingQuestionEdit[],
  ): Promise<void> => {
    if (!courseId || !changes?.customizedCourse) return;

    // Group question edits by content index
    const questionEditsByContent = pendingQuestionEdits.reduce((acc, edit) => {
      if (!acc[edit.contentIndex]) {
        acc[edit.contentIndex] = [];
      }
      acc[edit.contentIndex].push(edit);
      return acc;
    }, {} as Record<number, PendingQuestionEdit[]>);

    // Process question edits
    for (const [contentIndexStr, edits] of Object.entries(
      questionEditsByContent,
    )) {
      const contentIndex = parseInt(contentIndexStr, 10);
      const contentChange = changes.contentChanges[contentIndex];

      if (
        contentChange.type !== CourseContentType.QUIZ ||
        !contentChange.questionChanges
      ) {
        continue;
      }

      const courseContent = changes.customizedCourse.courseContents?.find(
        (cc) => cc.position === contentChange.position,
      );

      if (!courseContent?.quiz?.id) {
        continue;
      }

      // Use existing quiz if it has questions, otherwise fetch it
      const quiz = courseContent.quiz;

      // Update questions based on edits
      // Note: We must send ALL questions to prevent backend from deleting non-edited questions
      for (const edit of edits) {
        const questionChange =
          contentChange.questionChanges[edit.questionIndex];
        if (!questionChange) continue;

        // Find question by position (position in QuestionChange is 1-based, questions are 0-based)
        const questionPosition = questionChange.position - 1;
        const question = quiz.questions.find(
          (q) => q.position === questionPosition,
        );

        // Find translation for target language
        const translation = question?.translations.find(
          (t) => t.language === language,
        );

        if (!translation || !question) {
          continue;
        }

        // Update the appropriate field
        switch (edit.field) {
          case 'text':
            translation.text = edit.value as string;
            break;
          case 'explanation':
            translation.explanation = edit.value as string;
            break;
          case 'answers':
            // Handle answer options update
            if (Array.isArray(edit.value) && question.answerOptions) {
              const answers = edit.value as string[];
              const correctAnswers = edit.correctAnswers || [];

              // Update answer options (only if translation exists)
              question.answerOptions.forEach((answerOption, idx) => {
                if (idx < answers.length) {
                  const answerTranslation = answerOption.translations.find(
                    (t) => t.language === language,
                  );

                  // Skip if translation doesn't exist - we only update, never create
                  if (!answerTranslation) {
                    return;
                  }

                  answerTranslation.text = answers[idx];
                  answerOption.isCorrect = correctAnswers[idx] || false;
                }
              });
            }
            break;
          case 'ieSender':
          case 'ieMessage':
          case 'ieHeader':
          case 'ieBody': {
            if (!translation.interactiveElement) break;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const ieData = {
              ...((translation.interactiveElement.data as any) || {}),
            };
            if (edit.field === 'ieSender') ieData.sender = edit.value;
            if (edit.field === 'ieMessage') ieData.message = edit.value;
            if (edit.field === 'ieHeader') ieData.header = edit.value;
            if (edit.field === 'ieBody') ieData.body = edit.value;
            translation.interactiveElement = {
              ...translation.interactiveElement,
              data: ieData,
            };
            break;
          }
        }
      }

      // Build payload following the same pattern as ContentQuizModal
      const courseAccount = changes.customizedCourse.account;
      const courseLanguages = changes.customizedCourse.courseLanguages;
      const isSurvey = quiz.type === QuizType.SURVEY;

      const modifiedQuiz = modifyCourseQuiz(quiz);
      const modifiedQuestions = modifiedQuiz?.questions || [];

      const convertedQuestions = convertQuestionsToArrayFormat(
        modifiedQuestions,
        isSurvey,
        courseLanguages,
      );

      const quizData = {
        title: quiz.title,
        questions: convertedQuestions,
        supportedLanguages: courseLanguages,
        type: quiz.type,
        account:
          courseAccount?.isSystem || !courseAccount?.id
            ? null
            : courseAccount.id,
      };

      // Update quiz
      await client.quizzes.updateQuiz(courseContent.quiz.id, quizData);
    }
  };

  const saveChanges = async (
    pendingSlideEdits: PendingSlideEdit[],
    pendingQuestionEdits: PendingQuestionEdit[],
  ): Promise<void> => {
    try {
      await saveSlideChanges(pendingSlideEdits);
      await saveQuestionChanges(pendingQuestionEdits);
      await refetch();

      toast?.success('Success', 'Changes saved successfully');
    } catch (error) {
      handleAxiosError(error as AxiosError, toast);
      throw error;
    }
  };

  return { saveChanges };
};
